import { ElementRef, Injectable, inject } from '@angular/core';

import type { FullScreenController } from '@core/prosemirror-legacy';
import type { DialogRef } from '@core/user-interface-legacy';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import { FullScreenDialogComponent } from './full-screen-dialog/full-screen-dialog.component';

@Injectable()
export class FullScreenService implements FullScreenController {
  private readonly elementRef = inject(ElementRef);
  private readonly dialogSvc = inject(DialogService);

  private readonly editorEl: Element;
  private readonly originalParent: Element | null = null;
  private readonly originalSibling: Element | null = null;

  private dialogRef: DialogRef | null = null;

  constructor() {
    const elementRef = this.elementRef;

    this.editorEl = elementRef.nativeElement;
    this.originalParent = this.editorEl.parentElement;
    this.originalSibling = this.editorEl.nextElementSibling;
  }

  public isEnabled() {
    return !!this.dialogRef;
  }

  public toggle() {
    if (!this.dialogRef) {
      this.enable();
    } else {
      this.disable();
    }
  }

  private enable() {
    this.dialogRef = this.dialogSvc.openCustom(FullScreenDialogComponent, {
      width: DialogWidth.FullWidth,
      backdropStyle: 'cover',
      showCloseButton: false,
      containerOptions: {
        hasPadding: false,
      },
    });
    this.editorToDialog();
    this.dialogRef.afterClosed.subscribe(() => {
      this.editorToOriginalPlace();
      this.dialogRef = null;
    });
  }

  private disable() {
    this.dialogRef?.close();
  }

  private editorToDialog() {
    this.editorEl.classList.add('full-screen');
    (<FullScreenDialogComponent>this.dialogRef!.componentInstance).element.appendChild(
      this.editorEl,
    );
  }

  private editorToOriginalPlace() {
    this.editorEl.classList.remove('full-screen');
    if (this.originalSibling) {
      this.originalParent!.insertBefore(this.editorEl, this.originalSibling);
    } else {
      this.originalParent!.appendChild(this.editorEl);
    }
  }
}
